import { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import trans, { isRTL } from "../../utils";
import AntdTable from "../antd/Table/Table";
import { popupcontext } from "../../context/Popupscontext";
import {
  useGetAllTeamsQuery,
  useDeactivateTeamMutation,
  useActivateTeamMutation,
} from "../../Redux/service/Teams/Teams";
import { filtrationcontext } from "../../context/Filtercontext";
import { formatDateString } from "../../JsHelpers/DateFormat";
import TeamsHead from "./TeamsHead";
import Actions from "../antd/Table/Actions";
import { Segmented, Tag } from "antd";
import BreadCrumb from "../Header/SecondHeader";
import FormTeam from "./FormTeam/FormTeam";
import usePermission from "../../hooks/usePermission";
import AccessDenied from "../AccessDenied";
import { toast } from "react-toastify";
import { TeamOutlined, UserOutlined } from "@ant-design/icons";
import TeamMembers from "./Segments/TeamMembers";
import AssignTeamPopup from "./TeamPopup/AssignTeamPopup";
import AssignMemberToTeamsPopup from "./TeamPopup/AssignMemberToTeamsPopup";
import AssignMembersToTeamPopup from "./TeamPopup/AssignMembersToTeamPopup";
import AssignCompanyPopup from "./TeamPopup/AssignCompanyPopup";
import FormTeamMember from "./FormTeam/FormTeamMember";

const options = [
  { label: "Teams", value: "teams", icon: <TeamOutlined /> },
  { label: "Team Members", value: "team-members", icon: <UserOutlined /> },
];

const Teams = () => {
  const [teamForm, setTeamForm] = useState(false);
  const [teamMemberForm, setTeamMemberForm] = useState(false);
  const [selected, setSelected] = useState("teams");
  const [form, setForm] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const {
    handleShow,
    deleteItem,
    setDeleteItem,
    setEdited,
    edited,
    currentTeamId,
    currentMemberId,
    isEditing,
    handleClose,
    isAssigningMembersToTeam,
  } = useContext(popupcontext);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState({
    projectId: null,
    status: "ACTIVE", // Default to ACTIVE
  });
  const {
    create,
    view,
    edit: canEdit,
    delete: canDelete,
  } = usePermission("SNAG");

  const buildQueryString = (filterObj) => {
    const query = Object.entries(filterObj)
      .filter(
        ([_, value]) => value !== null && value !== "" && value !== undefined,
      )
      .map(([key, value]) => `${key}=${value}`)
      .join("&");

    return query ? `?${query}` : "";
  };

  const queryString = buildQueryString(filter);
  const {
    limit,
    setLimit,
    setSort: SetSortFilter,
    searchQuery,
    setSearchQuery,
  } = useContext(filtrationcontext);
  const [sort, setSort] = useState({
    by: "",
    order: "",
  });
  const {
    data: TeamsData,
    isFetching,
    refetch,
  } = useGetAllTeamsQuery(
    {
      page: currentPage,
      limit: limit,
      search: searchQuery,
      sortby: sort.by,
      order: sort.order,
      queryString: queryString,
    },
    {
      skip: !currentPage || !limit,
      refetchOnMountOrArgChange: true,
    },
  );

  const [deactivateTeam, { isLoading: isDeactivating }] =
    useDeactivateTeamMutation();
  const [activateTeam, { isLoading: isActivating }] = useActivateTeamMutation();

  useEffect(() => {
    return () => {
      SetSortFilter({ type: "", value: "" });
      setSearchQuery("");
    };
  }, [SetSortFilter, setSearchQuery]);

  useEffect(() => {
    async function fetchdata() {
      if (edited) {
        await refetch();
        setEdited(false);
      }
      if (refresh) {
        await refetch();
        setRefresh(false);
      }
    }
    fetchdata();
  }, [edited, refresh, refetch, setEdited]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  useEffect(() => {
    localStorage.setItem("page", currentPage);
  }, [currentPage]);

  // Handle team editing from popupcontext
  useEffect(() => {
    if (isEditing && currentTeamId) {
      setTeamForm(currentTeamId);
    } else if (
      !isEditing &&
      currentTeamId === null &&
      typeof teamForm === "number"
    ) {
      // Reset form when editing is closed
      setTeamForm(false);
    }
  }, [isEditing, currentTeamId]);

  // Handle member editing from popupcontext
  useEffect(() => {
    if (isEditing && currentMemberId) {
      setTeamMemberForm(currentMemberId);
    } else if (
      !isEditing &&
      currentMemberId === null &&
      typeof teamMemberForm === "number"
    ) {
      // Reset form when editing is closed
      setTeamMemberForm(false);
    }
  }, [isEditing, currentMemberId]);

  const handleTableChange = (pagination, filters, sorter) => {
    if (sorter.order) {
      setSort({
        by: sorter.field,
        order: sorter.order === "ascend" ? "asc" : "desc",
      });
    } else {
      setSort({
        by: "",
        order: "",
      });
    }
  };

  useEffect(() => {
    if (deleteItem.condition === true && deleteItem.value === "team") {
      refetch();
      if (TeamsData?.data.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
        localStorage.setItem("page", currentPage);
      }
      setDeleteItem({ condition: null, value: null });
    }
  }, [
    currentPage,
    deleteItem.condition,
    deleteItem.value,
    refetch,
    setDeleteItem,
    TeamsData?.data,
  ]);

  const handleDeactivateTeam = async (teamId) => {
    try {
      await deactivateTeam(teamId).unwrap();
      toast.success(trans("Team deactivated successfully"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
      await refetch();
    } catch (error) {
      console.error("Deactivation error:", error);
      toast.error(error?.data?.message || trans("Failed to deactivate team"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
    }
  };

  const handleActivateTeam = async (teamId) => {
    try {
      await activateTeam(teamId).unwrap();
      toast.success(trans("Team activated successfully"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
      await refetch();
    } catch (error) {
      console.error("Activation error:", error);
      toast.error(error?.data?.message || trans("Failed to activate team"), {
        autoClose: 800,
        rtl: localStorage.getItem("lang") === "ar" ? true : false,
      });
    }
  };

  const columns = [
    {
      title: trans("Team Code"),
      dataIndex: "code",
      key: "code",
      width: 150,
      sorter: true,
    },
    {
      title: trans("Team Name"),
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: true,
      ellipsis: true,
    },
    {
      title: trans("Company"),
      dataIndex: "companyName",
      key: "companyName",
      width: 200,
      render: (companyName) => companyName || "-",
    },
    {
      title: trans("Project"),
      dataIndex: "projectName",
      key: "projectName",
      width: 200,
      render: (projectName) =>
        <Tag key={projectName.id}>{projectName.name}</Tag> || "-",
    },
    {
      title: trans("Status"),
      dataIndex: "status",
      key: "status",
      width: 150,
      render: (status) =>
        status === "ACTIVE" ? (
          <Tag color="green">{trans("Active")}</Tag>
        ) : (
          <Tag color="red">{trans("Inactive")}</Tag>
        ),
    },
    {
      title: trans("Trades"),
      dataIndex: "trades",
      key: "trades",
      width: 200,
      render: (trades) => {
        if (Array.isArray(trades) && trades.length > 0) {
          return trades?.map((trade) => <Tag key={trade.id}>{trade.name}</Tag>);
        }
        return "-";
      },
    },
    {
      title: trans("Assigned Zones"),
      dataIndex: "assignedZones",
      key: "assignedZones",
      width: 150,
      render: (zones) => {
        if (Array.isArray(zones) && zones.length > 0) {
          return zones.length;
        }
        return 0;
      },
    },
    {
      title: trans("Members Count"),
      dataIndex: "memberCount",
      key: "memberCount",
      width: 150,
    },
    {
      title: trans("Assigned Snags"),
      dataIndex: "snagCount",
      key: "snagCount",
      width: 150,
    },
    {
      title: trans("Actions"),
      width: 150,
      fixed: !isRTL ? "right" : undefined,
      render: (_, record) => (
        <Actions
          teamId={record.id}
          type="team"
          setEditTeam={setTeamForm}
          canEdit={canEdit}
          canDelete={canDelete}
          onDeactivateTeam={handleDeactivateTeam}
          onActivateTeam={handleActivateTeam}
          teamStatus={record.status}
        />
      ),
    },
  ];

  if (!view) {
    return <AccessDenied />;
  }

  return (
    <>
      <Helmet>
        <title>Teams</title>
        <meta name="description" content="Teams Management" />
      </Helmet>
      <BreadCrumb
        add={
          create
            ? selected === "teams"
              ? trans("Add new team")
              : trans("Add Team Member")
            : null
        }
        onClick={() => {
          if (selected === "teams") {
            setTeamForm(true);
          } else {
            setTeamMemberForm(true);
          }
        }}
        secButton={trans("Refresh")}
        secClick={() => setRefresh(true)}
        secLoading={isFetching}
      />
      <Segmented
        options={options?.map((i) => ({
          label: trans(i.label),
          value: i.value,
          icon: i.icon,
        }))}
        onChange={(value) => {
          setForm(false);
          setSearchQuery("");
          setSelected(value);
        }}
        value={selected}
        block
      />

      {selected === "teams" && (
        <>
          <TeamsHead setFilter={setFilter} filter={filter} />
          <div className="bg-white-rounded-top mt-3 table-parent">
            <AntdTable
              columns={columns}
              loading={isFetching}
              dataSource={TeamsData?.data || []}
              pageSize={limit}
              totalItems={TeamsData?.pagination?.total || 0}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setCurrentPage(1);
              }}
              onTableChange={handleTableChange}
              scrollX={true}
            />
          </div>
        </>
      )}
      {selected === "team-members" && (
        <>
          <TeamMembers />
        </>
      )}

      <FormTeam
        teamForm={teamForm}
        setTeamForm={setTeamForm}
        isEditing={typeof teamForm === "number"}
      />
      <FormTeamMember
        teamMemberForm={teamMemberForm}
        setTeamMemberForm={setTeamMemberForm}
        isEditing={typeof teamMemberForm === "number"}
      />
      <AssignTeamPopup setEdited={setEdited} />
      {selected === "teams" && <AssignMembersToTeamPopup />}
      {selected === "team-members" && (
        <>
          <AssignMemberToTeamsPopup />
          <AssignCompanyPopup setEdited={setEdited} />
        </>
      )}
    </>
  );
};

export default Teams;
